'use client';

import type { ClientTimedMediaMeta } from '../client-meta';

const MEDIA_METADATA_TIMEOUT_MS = 10_000;

function readMetaFromElement(
  element: HTMLMediaElement,
  objectUrl: string,
  kind: 'video' | 'audio'
): Promise<ClientTimedMediaMeta> {
  return new Promise((resolve, reject) => {
    let settled = false;
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      element.removeAttribute('src');
      element.load();
      URL.revokeObjectURL(objectUrl);
    };
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };
    const timeoutId = window.setTimeout(
      () =>
        finish(() =>
          reject(new Error('Timed media metadata loading timed out.'))
        ),
      MEDIA_METADATA_TIMEOUT_MS
    );

    element.preload = 'metadata';
    element.onloadedmetadata = () => {
      const durationSeconds = element.duration;
      const video = kind === 'video' ? (element as HTMLVideoElement) : null;
      const width = video?.videoWidth ?? 0;
      const height = video?.videoHeight ?? 0;
      finish(() => {
        if (!Number.isFinite(durationSeconds) || durationSeconds <= 0) {
          reject(new Error('Timed media duration is unavailable.'));
          return;
        }
        if (kind === 'video' && (width <= 0 || height <= 0)) {
          reject(new Error('Video resolution could not be read.'));
          return;
        }
        resolve({
          durationSeconds,
          ...(kind === 'video' ? { width, height } : {}),
        });
      });
    };
    element.onerror = () =>
      finish(() =>
        reject(new Error('Timed media metadata could not be read.'))
      );
    element.src = objectUrl;
  });
}

export async function readClientTimedMediaMeta(
  file: File,
  kind: 'video' | 'audio'
): Promise<ClientTimedMediaMeta> {
  const objectUrl = URL.createObjectURL(file);
  const element = document.createElement(kind);
  return readMetaFromElement(element, objectUrl, kind);
}
